// File Metadata:
// Filename: EditReceptionForm.js
// Version: 1.00
// Created: 2022-09-28 22:18:41
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import { useState, useEffect } from 'react'
import { useUpdateReceptionMutation, useDeleteReceptionMutation } from '../api/receptionsApiSlice'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../hooks/useAuth'
import { LOADERS } from '../../config/constants'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { 
    Box, Grid, Stack, Paper, Typography, TextField, 
    Button, FormControl, InputLabel, Select, MenuItem,
    Alert, IconButton, FormControlLabel, Checkbox,
    Card, CardHeader, CardContent, Divider,
    useTheme, useMediaQuery
} from '@mui/material'
import {
    Save as SaveIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { parseISO } from 'date-fns';

const EditReceptionForm = ({ reception, vendors }) => {
	const { username, isVoorman, isAdmin, isQuality } = useAuth()
	const [updateReception, { isLoading, isSuccess, isError, error }] = useUpdateReceptionMutation()
	const [deleteReception, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
		useDeleteReceptionMutation()

	const navigate = useNavigate()

	const [deliveryDate, setDeliveryDate] = useState(reception.deliveryDate)
	const [unloadLocation, setUnloadLocation] = useState(reception.unloadLocation)
	const [unloadedBy, setUnloadedBy] = useState(reception.unloadedBy)
	const [vendorNumber, setVendorNumber] = useState(reception.vendorNumber)
	const [vendorName, setVendorName] = useState(reception.vendorName)
	const [tourNumber, setTourNumber] = useState(reception.tourNumber)
	const [handlingUnits, setHandlingUnits] = useState({})
	const [receptionDate, setReceptionDate] = useState(reception.receptionDate)
	const [goodsReceipt, setGoodsReceipt] = useState(reception.goodsReceipt)
	const [putawayStatus, setPutawayStatus] = useState(reception.putawayStatus)
	const [notes, setNotes] = useState(reception.notes)

	useEffect(() => {
		if (isSuccess || isDelSuccess) {
			setDeliveryDate('')
			setUnloadLocation('')
			setUnloadedBy('')
			setVendorNumber('')
			setVendorName('')
			setTourNumber('')
			setHandlingUnits({})
			setReceptionDate('')
			setGoodsReceipt(false)
			setPutawayStatus(false)
			setNotes('')
			navigate('/dash/receptions')
		}
	}, [isSuccess, isDelSuccess, navigate])

	const onDeliveryDateChanged = e => setDeliveryDate(e.target.value)
	const onUnloadLocationChanged = e => setUnloadLocation(e.target.value)
	const onUnloadedByChanged = e => setUnloadedBy(e.target.value)
	const onVendorNumberChanged = e => setVendorNumber(e.target.value)
	const onVendorNameChanged = e => {
		setVendorName(e.target.value)
		let foundVendor = vendors.find(vendor => {
			if (vendor.vendorName === e.target.value) return vendor.vendorNumber
		})
		console.log(foundVendor)
		setVendorNumber(foundVendor.vendorNumber)
	}
	const onTourNumberChanged = e => setTourNumber(e.target.value)
	const onHandlingUnitsChanged = e => setHandlingUnits(e.target.value)
	const onReceptionDateChanged = e => setReceptionDate(e.target.value)
	const onGoodsReceiptChanged = e => setGoodsReceipt(prev => !prev)
	const onPutawayStatusChanged = e => setPutawayStatus(prev => !prev)
	const onNotesChanged = e => setNotes(e.target.value)

	const canSave = [deliveryDate, unloadLocation, unloadedBy, vendorName].every(Boolean) && !isLoading

	const onSaveReceptionClicked = async e => {
		if (canSave) {
			try {
				// Remove modifiedBy from update payload since it's causing validation issues
				await updateReception({
					id: reception.id,
					deliveryDate,
					unloadLocation,
					unloadedBy,
					vendorNumber,
					vendorName,
					tourNumber,
					handlingUnits,
					receptionDate,
					goodsReceipt,
					putawayStatus,
					notes,
					inboundCompleted: false,
					inboundStatus: ['1'],
					registrationBy: username,  // Keep these basic fields
					receptionBy: '',
					putawayBy: '',
					qualityStatus: reception.qualityStatus // Keep existing quality status
				})
			} catch (err) {
				console.error('Failed to update:', err)
			}
		}
	}

	const onDeleteReceptionClicked = async () => {
		await deleteReception({ id: reception.id })
	}

	const created = new Date(reception.createdAt).toLocaleString('en-US', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric'
	})
	const updated = new Date(reception.updatedAt).toLocaleString('en-US', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: 'numeric',
		minute: 'numeric',
		second: 'numeric'
	})

	const unloadedByOptions = LOADERS.map(unloader => {
		return (
			<option key={unloader.id} value={unloader.name}>
				{unloader.name}
			</option>
		)
	})
	const vendorNameOptions = vendors.map(vendor => {
		return (
			<option key={vendor.id} value={vendor.vendorName}>
				{vendor.vendorName}
			</option>
		)
	})

	const errClass = isError ? 'errmsg' : 'offscreen'
	const validDeliveryDateClass = !deliveryDate ? 'form__input--incomplete' : ''
	const validUnloadLocationClass = !unloadLocation ? 'form__input--incomplete' : ''
	const validUnloadedByClass = !unloadedBy ? 'form__input--incomplete' : ''
	const validVendorNameClass = !vendorName ? 'form__input--incomplete' : ''

	const errContent = (error?.data?.message || delerror?.data?.message) ?? ''

	let deleteButton = null
	if (isVoorman || isAdmin) {
		deleteButton = (
			<button className='icon-button' title='Delete' onClick={onDeleteReceptionClicked}>
				<FontAwesomeIcon icon={faTrashCan} />
			</button>
		)
	}

	const saveButton = (
		<button className='icon-button' title='Save' onClick={onSaveReceptionClicked} disabled={!canSave}>
			<FontAwesomeIcon icon={faSave} />
		</button>
	)

	/* start datagrid data for test */

	/*  */

	// Add approval status checks
	const canEditFields = reception.qualityStatus !== 'REJECTED' && 
						 (isAdmin || isQuality || isVoorman)
	
	const canUpdateGoodsReceipt = reception.qualityStatus === 'APPROVED' || reception.qualityStatus === 'CONDITIONALLY_APPROVED'

    const canUpdatePutawayStatus = reception.qualityStatus === 'APPROVED' || reception.qualityStatus === 'CONDITIONALLY_APPROVED'

    // Add responsive breakpoint check
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    return (
        <Grid container spacing={3}>
            {/* Main Form Content */}
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3 }}>
                    <CardHeader 
                        title={`Edit Reception #${reception.reception}`}
                        // Remove action buttons from here since we have them in form content
                    />
                    <CardContent>
                        {(isError || isDelError) && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error?.data?.message || delerror?.data?.message}
                            </Alert>
                        )}

                        <Stack spacing={3}>
                            <TextField
                                label="Delivery Date"
                                type="date"
                                value={deliveryDate}
                                onChange={onDeliveryDateChanged}
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                error={!deliveryDate}
                                required
                            />

                            <TextField
                                label="Unload Location"
                                value={unloadLocation}
                                onChange={onUnloadLocationChanged}
                                fullWidth
                                error={!unloadLocation}
                                required
                            />

                            <FormControl fullWidth required>
                                <InputLabel>Unloaded By</InputLabel>
                                <Select
                                    value={unloadedBy}
                                    label="Unloaded By"
                                    onChange={onUnloadedByChanged}
                                    error={!unloadedBy}
                                >
                                    {LOADERS.map(loader => (
                                        <MenuItem key={loader.id} value={loader.name}>
                                            {loader.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth required>
                                <InputLabel>Vendor</InputLabel>
                                <Select
                                    value={vendorName}
                                    label="Vendor"
                                    onChange={onVendorNameChanged}
                                    error={!vendorName}
                                >
                                    {vendorNameOptions}
                                </Select>
                            </FormControl>

                            <TextField
                                label="Vendor Number"
                                value={vendorNumber}
                                disabled
                                fullWidth
                            />

                            <TextField
                                label="Tour Number"
                                value={tourNumber}
                                onChange={onTourNumberChanged}
                                fullWidth
                            />

                            <TextField
                                label="Reception Notes"
                                multiline
                                rows={4}
                                value={notes}
                                onChange={onNotesChanged}
                                fullWidth
                            />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <IconButton
                                    onClick={onSaveReceptionClicked}
                                    disabled={!canSave}
                                    color="primary"
                                >
                                    <SaveIcon />
                                </IconButton>
                                {(isVoorman || isAdmin) && (
                                    <IconButton
                                        onClick={onDeleteReceptionClicked}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                )}
                            </Box>
                        </Stack>
                    </CardContent>
                </Paper>
            </Grid>

            {/* Status Panel */}
            <Grid item xs={12} md={4}>
                <Paper sx={{ p: 3 }}>
                    <Typography variant="h6" gutterBottom>
                        Status
                    </Typography>
                    <Stack spacing={2}>
					<LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label="Reception Date"
                                value={receptionDate ? parseISO(receptionDate) : null}
                                onChange={(newValue) => {
                                    setReceptionDate(newValue ? newValue.toISOString().split('T')[0] : '')
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        fullWidth
                                        required={false}
                                    />
                                )}
                            />
                        </LocalizationProvider>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={goodsReceipt}
                                    onChange={onGoodsReceiptChanged}
                                    disabled={!canUpdateGoodsReceipt}
                                />
                            }
                            label="Goods Receipt"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={putawayStatus}
                                    onChange={onPutawayStatusChanged}
                                    disabled={!canUpdatePutawayStatus}
                                    color="primary"
                                />
                            }
                            label="Putaway Status"
                        />

                        {!canUpdateGoodsReceipt && (
                            <Alert severity="warning" variant="filled">
                                Quality approval required before goods receipt
                            </Alert>
                        )}
                    </Stack>
                </Paper>
            </Grid>
        </Grid>
    )
}

export default EditReceptionForm
